import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import EditIcon from '@mui/icons-material/Edit';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import StraightenIcon from '@mui/icons-material/Straighten';
import { VIEWER_MODES } from 'components/ImageViewer/hooks';
import { useViewerModeBannerStyles } from 'components/ImageViewer/components/ViewerModeBanner/styles';

const DRAW_TOOLS = {
  rect: { Icon: CropSquareIcon, label: 'a square' },
  ellipse: { Icon: RadioButtonUncheckedIcon, label: 'a circle' },
  freehand: { Icon: EditIcon, label: 'a pencil stroke' },
};

/**
 * What the banner says for the current state, or null when the viewer is in
 * plain select mode with nothing selected (then there is nothing to leave).
 */
export const getViewerModeBannerContent = ({
  mode,
  drawTool,
  hasSelection,
}) => {
  if (mode === VIEWER_MODES.DRAW) {
    const { Icon, label } = DRAW_TOOLS[drawTool] || DRAW_TOOLS.rect;

    return {
      Icon,
      title: 'Drawing',
      detail: `drag on the slide to add ${label}`,
      exitLabel: 'Exit',
    };
  }

  if (mode === VIEWER_MODES.MEASURE) {
    return {
      Icon: StraightenIcon,
      title: 'Measuring',
      detail: 'click two points on the slide',
      exitLabel: 'Exit',
    };
  }

  if (hasSelection) {
    return {
      Icon: HighlightAltIcon,
      title: 'Annotation selected',
      detail: 'drag to move it · Del deletes it',
      exitLabel: 'Deselect',
    };
  }

  return null;
};

/**
 * Always-visible reminder of the annotation state the user is in and how to
 * leave it: a pill at the bottom of the slide with an Esc key cap. Pressing
 * Esc (useViewerModeKeyboardShortcuts) and clicking the button do the same.
 */
export const ViewerModeBanner = ({ mode, drawTool, hasSelection, onExit }) => {
  const { classes } = useViewerModeBannerStyles();
  const content = getViewerModeBannerContent({ mode, drawTool, hasSelection });

  if (!content) {
    return null;
  }

  const { Icon, title, detail, exitLabel } = content;

  return (
    <Box
      className={classes.banner}
      role="status"
      aria-live="polite"
      data-testid="viewer-mode-banner"
    >
      <Icon className={classes.icon} fontSize="inherit" />
      <span className={classes.title}>{title}</span>
      <span className={classes.detail}>{detail}</span>
      <ButtonBase
        className={classes.exitButton}
        onClick={onExit}
        aria-label={`${exitLabel} (Esc)`}
        focusRipple
      >
        <kbd className={classes.key}>Esc</kbd>
        <span>{exitLabel}</span>
        <CloseIcon className={classes.exitIcon} fontSize="inherit" />
      </ButtonBase>
    </Box>
  );
};
